import { createContext, useContext, useRef, useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const PlayerContext = createContext(null);

// PlayerState codes from YouTube IFrame API
const YT_PLAYING = 1;
const YT_ENDED = 0;
const YT_BUFFERING = 3;

const HTML_AUDIO_MODES = new Set(["radio", "track"]);
const MEDIA_SESSION_MODES = new Set(["radio", "track", "yt-audio"]);

/**
 * Determine the playback URL for a stream. If the stream is HTTP (not HTTPS)
 * or known to have CORS issues, route through our backend audio proxy.
 * Never proxy YouTube. Prefer direct HTTPS when possible.
 */
function resolveStreamUrl(rawUrl) {
  if (!rawUrl) return rawUrl;
  try {
    const u = new URL(rawUrl);
    // Direct HTTPS streams work fine without proxy
    if (u.protocol === "https:") return rawUrl;
    // HTTP streams need proxy due to mixed-content blocking on HTTPS page
    if (u.protocol === "http:") {
      return `/api/audio/proxy?url=${encodeURIComponent(rawUrl)}`;
    }
  } catch (_) { /* malformed URL, pass through */ }
  return rawUrl;
}

function safeTitle(current, mode) {
  if (!current) return "Recho Music";
  if (mode === "radio") return current.name || "Live radio";
  return current.title || current.name || "Recho Music";
}

function safeArtist(current, mode) {
  if (!current) return "NeurAlpha";
  if (mode === "radio") return current.country || "Worldwide radio";
  if (mode === "yt-audio") return current.artist || current.channel || "YouTube Music";
  return current.artist || current.channel || current.source || "Recho Music";
}

function safeAlbum(current, mode) {
  if (!current) return "Recho Music";
  if (mode === "radio") return "Live Radio · Recho Music";
  if (mode === "yt-audio") return "YouTube Music · Recho Music";
  return current.album || `${current.source || "Free Music"} · Recho Music`;
}

function artworkFor(current, mode) {
  const src =
    mode === "radio" ? current?.favicon :
    mode === "track" ? current?.artwork :
    mode === "yt-audio" ? (current?.artwork || current?.thumbnail) :
    current?.thumbnail;

  const fallback = "/icon-512.svg";
  return [
    { src: src || fallback, sizes: "512x512" },
    { src: fallback, sizes: "512x512", type: "image/svg+xml" },
  ];
}

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const sleepTimerRef = useRef(null);
  const ytPlayerRef = useRef(null);             // set by <YTAudioController/>
  const pendingYTRef = useRef(null);            // queued play if YT API not ready
  const volumeRef = useRef(0.8);
  const currentRef = useRef(null);
  const modeRef = useRef("radio");
  const isPlayingRef = useRef(false);
  const userPausedRef = useRef(false);

  const [current, setCurrent] = useState(null);
  const [mode, setMode] = useState("radio");    // 'radio' | 'video' | 'track' | 'yt-audio'
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [expanded, setExpanded] = useState(false);
  const [sleepMinutes, setSleepMinutes] = useState(0);
  const [sleepEndsAt, setSleepEndsAt] = useState(null);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [ytReady, setYtReady] = useState(false);
  const [backgroundAudioReady, setBackgroundAudioReady] = useState(false);

  useEffect(() => { currentRef.current = current; }, [current]);
  useEffect(() => { modeRef.current = mode; }, [mode]);
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);

  const ensureAudioElement = useCallback(() => {
    if (audioRef.current) return audioRef.current;

    if (typeof document !== "undefined") {
      let el = document.querySelector("audio[data-recho-background-audio]");
      if (!el) {
        el = document.createElement("audio");
        el.setAttribute("data-recho-background-audio", "true");
        el.setAttribute("preload", "none");
        el.setAttribute("playsinline", "true");
        el.setAttribute("x-webkit-airplay", "allow");
        el.controls = false;
        el.autoplay = false;
        el.style.position = "fixed";
        el.style.left = "-9999px";
        el.style.width = "1px";
        el.style.height = "1px";
        el.style.opacity = "0";
        el.style.pointerEvents = "none";
        document.body.appendChild(el);
      }
      audioRef.current = el;
    } else if (typeof Audio !== "undefined") {
      audioRef.current = new Audio();
    }

    if (audioRef.current) {
      audioRef.current.volume = volumeRef.current;
      setBackgroundAudioReady(true);
    }
    return audioRef.current;
  }, []);

  // Init shared HTMLAudio and keep volume synced.
  useEffect(() => {
    const el = ensureAudioElement();
    if (el) {
      el.volume = volume;
      volumeRef.current = volume;
    }
  }, [volume, ensureAudioElement]);

  // Sync YT volume on slider change.
  useEffect(() => {
    const p = ytPlayerRef.current;
    if (p && p.setVolume) {
      try { p.setVolume(Math.round(volume * 100)); } catch (_) { /* ignore */ }
    }
  }, [volume]);

  // HTMLAudio events are attached once and read the latest mode through refs.
  useEffect(() => {
    const el = ensureAudioElement();
    if (!el) return undefined;

    const onTime = () => {
      if (HTML_AUDIO_MODES.has(modeRef.current)) setProgress(el.currentTime || 0);
    };
    const onMeta = () => {
      if (HTML_AUDIO_MODES.has(modeRef.current)) {
        setDuration(Number.isFinite(el.duration) ? el.duration : 0);
      }
    };
    const onPlay = () => {
      if (HTML_AUDIO_MODES.has(modeRef.current)) setIsPlaying(true);
    };
    const onPause = () => {
      if (!HTML_AUDIO_MODES.has(modeRef.current)) return;
      // If user deliberately paused, accept it immediately.
      if (userPausedRef.current) {
        setIsPlaying(false);
        return;
      }
      // If page is hidden (screen lock / tab switch), the browser may pause audio.
      // The keepDirectAudioAlive handler in the visibility change listener will
      // attempt recovery. Avoid duplicate recovery here — just update state.
      const hidden = typeof document !== "undefined" && document.visibilityState === "hidden";
      if (hidden && currentRef.current && el.src) {
        // Don't immediately set isPlaying=false; give visibility handler a chance.
        setTimeout(() => {
          if (el.paused && !userPausedRef.current && currentRef.current) {
            setIsPlaying(false);
          }
        }, 500);
        return;
      }
      setIsPlaying(false);
    };
    const onEnd = () => {
      if (HTML_AUDIO_MODES.has(modeRef.current)) setIsPlaying(false);
    };
    const onError = () => {
      if (HTML_AUDIO_MODES.has(modeRef.current)) {
        setIsPlaying(false);
        const cur = currentRef.current;
        if (cur) {
          const name = cur.name || cur.title || "Stream";
          toast.error(`${name} failed to load. Try another.`);
        }
      }
    };

    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onMeta);
    el.addEventListener("durationchange", onMeta);
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("ended", onEnd);
    el.addEventListener("error", onError);

    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onMeta);
      el.removeEventListener("durationchange", onMeta);
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ended", onEnd);
      el.removeEventListener("error", onError);
    };
  }, [ensureAudioElement]);

  const stopYouTube = useCallback(() => {
    const p = ytPlayerRef.current;
    if (p && p.stopVideo) {
      try { p.stopVideo(); } catch (_) { /* ignore */ }
    }
  }, []);

  // YT poll loop for progress/duration/state (only when in yt-audio mode).
  useEffect(() => {
    if (mode !== "yt-audio") return undefined;
    const id = setInterval(() => {
      const p = ytPlayerRef.current;
      if (!p || !p.getCurrentTime) return;
      try {
        const t = p.getCurrentTime();
        const d = p.getDuration();
        if (Number.isFinite(t)) setProgress(t);
        if (Number.isFinite(d) && d > 0) setDuration(d);
        const state = p.getPlayerState ? p.getPlayerState() : null;
        if (state === YT_PLAYING && !isPlayingRef.current) setIsPlaying(true);
        if (state !== YT_PLAYING && state !== YT_BUFFERING && isPlayingRef.current) setIsPlaying(false);
        if (state === YT_ENDED) setIsPlaying(false);
      } catch (_) { /* ignore */ }
    }, 500);
    return () => clearInterval(id);
  }, [mode]);

  // Fire-and-forget history logging.
  const logHistory = useCallback(async (kind, item) => {
    try {
      const payload = { kind, item_id: "", name: "", metadata: {} };
      if (kind === "radio") {
        payload.item_id = item.stationuuid; payload.name = item.name;
        payload.metadata = {
          country: item.country || "", tags: item.tags || "",
          favicon: item.favicon || "", url: item.url_resolved || item.url,
        };
      } else if (kind === "video") {
        payload.item_id = item.video_id; payload.name = item.title;
        payload.metadata = { thumbnail: item.thumbnail, channel: item.channel };
      } else if (kind === "track") {
        payload.item_id = item.track_id; payload.name = item.title;
        payload.metadata = {
          source: item.source, artist: item.artist, album: item.album,
          artwork: item.artwork, stream_url: item.stream_url, duration: item.duration,
        };
      }
      await api.post("/history", payload);
    } catch (_) { /* not logged-in or offline */ }
  }, []);

  const getCurrentPlaybackTime = useCallback(() => {
    const activeMode = modeRef.current;
    if (activeMode === "yt-audio") {
      const p = ytPlayerRef.current;
      try {
        const t = p?.getCurrentTime?.();
        return Number.isFinite(t) ? t : progress;
      } catch (_) { return progress; }
    }
    const el = audioRef.current;
    return Number.isFinite(el?.currentTime) ? el.currentTime : progress;
  }, [progress]);

  // ============ ACTIONS ============
  const playStation = useCallback((station) => {
    const el = ensureAudioElement();
    if (!el) return;

    pendingYTRef.current = null;
    userPausedRef.current = false;
    stopYouTube();

    el.pause();
    el.preload = "none";
    const streamUrl = resolveStreamUrl(station.url_resolved || station.url);
    el.src = streamUrl;

    const nextCurrent = { ...station, _type: "radio" };
    currentRef.current = nextCurrent;
    setCurrent(nextCurrent);
    setMode("radio");
    setProgress(0); setDuration(0);

    // Start playback synchronously within the user gesture so the browser
    // grants background/lock-screen media playback. Async play() calls lose
    // the user-activation token and get killed when the tab is hidden.
    el.play()
      .then(() => { setIsPlaying(true); logHistory("radio", station); })
      .catch((e) => {
        if (e.name !== "AbortError") {
          console.error("Play error", e);
        }
        // Fallback: some streams need data loaded first; retry once on canplay.
        const onCanPlay = () => {
          el.removeEventListener("canplay", onCanPlay);
          el.removeEventListener("error", onLoadError);
          if (currentRef.current !== nextCurrent) return; // user changed source
          el.play()
            .then(() => { setIsPlaying(true); logHistory("radio", station); })
            .catch((err) => {
              if (err.name !== "AbortError") {
                console.error("Play error", err);
                toast.error("Could not play this station. Try another.");
              }
              setIsPlaying(false);
            });
        };
        const onLoadError = () => {
          el.removeEventListener("canplay", onCanPlay);
          el.removeEventListener("error", onLoadError);
          if (currentRef.current !== nextCurrent) return;
          setIsPlaying(false);
          toast.error("Station stream unavailable. Try another.");
        };
        el.addEventListener("canplay", onCanPlay, { once: true });
        el.addEventListener("error", onLoadError, { once: true });
        el.load();
      });
  }, [ensureAudioElement, logHistory, stopYouTube]);

  // playVideo: foreground YouTube video (used by Videos page only). Videos are intentionally not background-enabled.
  const playVideo = useCallback((video) => {
    const el = ensureAudioElement();
    if (el) el.pause();
    pendingYTRef.current = null;
    userPausedRef.current = true;
    stopYouTube();
    setIsPlaying(false);
    const nextCurrent = { ...video, _type: "video" };
    currentRef.current = nextCurrent;
    setCurrent(nextCurrent);
    setMode("video");
    setProgress(0); setDuration(0);
    logHistory("video", video);
  }, [ensureAudioElement, logHistory, stopYouTube]);

  // Helper: mute → playVideo → unmute. Standard workaround for cross-origin
  // autoplay restrictions when using the YT IFrame Player API.
  const ytForcePlay = useCallback((p) => {
    if (!p) return;
    userPausedRef.current = false;
    try {
      p.mute && p.mute();
      setTimeout(() => {
        try {
          p.playVideo && p.playVideo();
          setTimeout(() => {
            try {
              if (volumeRef.current > 0) {
                p.unMute && p.unMute();
                p.setVolume(Math.round(volumeRef.current * 100));
              }
            } catch (_) { /* ignore */ }
          }, 800);
        } catch (_) { /* ignore */ }
      }, 120);
    } catch (_) { /* ignore */ }
  }, []);

  // playTrack: dispatcher — direct MP3/stream via HTML5 audio, YouTube via IFrame API.
  const playTrack = useCallback((track) => {
    if (track.source === "youtube") {
      const videoId = track.video_id || (track.track_id || "").split(":")[1];
      if (!videoId) return;
      const el = ensureAudioElement();
      if (el) el.pause();
      userPausedRef.current = false;
      const nextCurrent = { ...track, video_id: videoId, _type: "yt-audio" };
      currentRef.current = nextCurrent;
      setCurrent(nextCurrent);
      setMode("yt-audio");
      setProgress(0); setDuration(0);
      const p = ytPlayerRef.current;
      if (p && p.loadVideoById) {
        try {
          p.loadVideoById(videoId);
          ytForcePlay(p);
          setIsPlaying(true);
          logHistory("video", { video_id: videoId, title: track.title, thumbnail: track.artwork || track.thumbnail || "", channel: track.artist || track.channel || "" });
        } catch (e) { console.error("YT play error", e); setIsPlaying(false); }
      } else {
        pendingYTRef.current = { video_id: videoId, title: track.title, thumbnail: track.artwork || "", channel: track.artist || "" };
        setIsPlaying(false);
      }
      return;
    }

    const el = ensureAudioElement();
    if (!el) return;
    pendingYTRef.current = null;
    userPausedRef.current = false;
    stopYouTube();

    el.pause();
    el.preload = "auto";
    const streamUrl = resolveStreamUrl(track.stream_url);
    el.src = streamUrl;

    const nextCurrent = { ...track, _type: "track" };
    currentRef.current = nextCurrent;
    setCurrent(nextCurrent);
    setMode("track");
    setProgress(0); setDuration(track.duration || 0);

    // Start playback synchronously within the user gesture so the browser
    // grants background/lock-screen media playback. Async play() calls lose
    // the user-activation token and get killed when the tab is hidden.
    el.play()
      .then(() => { setIsPlaying(true); logHistory("track", track); })
      .catch((e) => {
        if (e.name !== "AbortError") {
          console.error("Track play error", e);
        }
        // Fallback: some streams need data loaded first; retry once on canplay.
        const onCanPlay = () => {
          el.removeEventListener("canplay", onCanPlay);
          el.removeEventListener("error", onLoadError);
          if (currentRef.current !== nextCurrent) return;
          el.play()
            .then(() => { setIsPlaying(true); logHistory("track", track); })
            .catch((err) => {
              if (err.name !== "AbortError") {
                console.error("Track play error", err);
                toast.error("Could not play this track. Try another.");
              }
              setIsPlaying(false);
            });
        };
        const onLoadError = () => {
          el.removeEventListener("canplay", onCanPlay);
          el.removeEventListener("error", onLoadError);
          if (currentRef.current !== nextCurrent) return;
          setIsPlaying(false);
          toast.error("Track unavailable. Try another.");
        };
        el.addEventListener("canplay", onCanPlay, { once: true });
        el.addEventListener("error", onLoadError, { once: true });
        el.load();
      });
  }, [ensureAudioElement, logHistory, stopYouTube, ytForcePlay]);

  // playYouTubeMusic: audio-first YouTube playback through hidden iframe.
  const playYouTubeMusic = useCallback((item) => {
    const el = ensureAudioElement();
    if (el) el.pause();
    userPausedRef.current = false;
    const nextCurrent = { ...item, _type: "yt-audio" };
    currentRef.current = nextCurrent;
    setCurrent(nextCurrent);
    setMode("yt-audio");
    setProgress(0); setDuration(0);

    const p = ytPlayerRef.current;
    if (p && p.loadVideoById) {
      try {
        p.loadVideoById(item.video_id);
        ytForcePlay(p);
        setIsPlaying(true);
        logHistory("video", { video_id: item.video_id, title: item.title, thumbnail: item.thumbnail, channel: item.channel });
      } catch (e) {
        console.error("YT play error", e);
        setIsPlaying(false);
      }
    } else {
      pendingYTRef.current = item;
      setIsPlaying(false);
    }
  }, [ensureAudioElement, logHistory, ytForcePlay]);

  const registerYTPlayer = useCallback((p) => {
    ytPlayerRef.current = p;
    setYtReady(true);
    if (pendingYTRef.current && p && p.loadVideoById) {
      try {
        const item = pendingYTRef.current;
        userPausedRef.current = false;
        p.loadVideoById(item.video_id);
        ytForcePlay(p);
        setIsPlaying(true);
        logHistory("video", { video_id: item.video_id, title: item.title, thumbnail: item.thumbnail, channel: item.channel });
      } catch (_) { /* ignore */ }
      pendingYTRef.current = null;
    }
  }, [logHistory, ytForcePlay]);

  // Called from YTAudioController when YT IFrame throws an error.
  // Codes: 2 invalid param, 5 HTML5 error, 100 not found, 101/150 embed disabled.
  const notifyYTError = useCallback((code) => {
    setIsPlaying(false);
    if (code === 101 || code === 150) {
      const failedId = currentRef.current?.video_id;
      // Dispatch event so Music page can auto-skip to next track in the visible list.
      if (failedId && typeof window !== "undefined") {
        try {
          window.dispatchEvent(new CustomEvent("recho-music:yt-embed-blocked", { detail: { video_id: failedId } }));
        } catch (_) { /* ignore */ }
      }
      // If no handler picks up the skip within 1.2s, clear current so PlayerBar dismisses cleanly.
      setTimeout(() => {
        const cur = currentRef.current;
        if (cur && cur._type === "yt-audio" && cur.video_id === failedId) {
          currentRef.current = null;
          setCurrent(null);
          setMode("radio");
          setProgress(0); setDuration(0);
        }
      }, 1200);
    } else if (code === 100) {
      toast.error("Video not found or removed.");
    } else if (code) {
      toast.error(`YouTube playback error (${code}). Try another track.`);
    }
  }, []);

  const toggle = useCallback(() => {
    const activeMode = modeRef.current;
    const activeCurrent = currentRef.current;
    if (activeMode === "video") return;

    if (activeMode === "yt-audio") {
      const p = ytPlayerRef.current;
      if (!p) return;
      try {
        if (isPlayingRef.current) {
          userPausedRef.current = true;
          p.pauseVideo();
          setIsPlaying(false);
        } else {
          userPausedRef.current = false;
          p.playVideo();
          setIsPlaying(true);
        }
      } catch (_) { /* ignore */ }
      return;
    }

    const el = ensureAudioElement();
    if (!el || !activeCurrent) return;
    if (isPlayingRef.current) {
      userPausedRef.current = true;
      el.pause();
      setIsPlaying(false);
    } else {
      userPausedRef.current = false;
      el.play()
        .then(() => setIsPlaying(true))
        .catch((e) => {
          if (e.name !== "AbortError") {
            toast.error("Playback failed. Try selecting the track again.");
          }
          setIsPlaying(false);
        });
    }
  }, [ensureAudioElement]);

  const seek = useCallback((sec) => {
    const activeMode = modeRef.current;
    if (activeMode === "video") return;
    if (!Number.isFinite(sec)) return;
    if (activeMode === "yt-audio") {
      const p = ytPlayerRef.current;
      if (p && p.seekTo) { try { p.seekTo(Math.max(0, sec), true); } catch (_) { /* ignore */ } }
      return;
    }
    const el = audioRef.current;
    if (el) el.currentTime = Math.max(0, sec);
  }, []);

  const stop = useCallback(() => {
    pendingYTRef.current = null;
    currentRef.current = null;
    userPausedRef.current = true;
    const el = audioRef.current;
    if (el) {
      el.pause();
      el.removeAttribute("src");
      try { el.load(); } catch (_) { /* ignore */ }
    }
    stopYouTube();
    setIsPlaying(false);
    setCurrent(null);
    setMode("radio");
    setExpanded(false);
    setProgress(0); setDuration(0);
    if (sleepTimerRef.current) { clearTimeout(sleepTimerRef.current); sleepTimerRef.current = null; }
    setSleepMinutes(0); setSleepEndsAt(null);
    if (typeof navigator !== "undefined" && navigator.mediaSession) {
      try { navigator.mediaSession.playbackState = "none"; } catch (_) { /* ignore */ }
    }
  }, [stopYouTube]);

  const setSleepTimer = useCallback((minutes) => {
    if (sleepTimerRef.current) { clearTimeout(sleepTimerRef.current); sleepTimerRef.current = null; }
    if (!minutes || minutes <= 0) { setSleepMinutes(0); setSleepEndsAt(null); return; }
    setSleepMinutes(minutes);
    const ends = Date.now() + minutes * 60 * 1000;
    setSleepEndsAt(ends);
    sleepTimerRef.current = setTimeout(() => {
      userPausedRef.current = true;
      if (audioRef.current) audioRef.current.pause();
      const p = ytPlayerRef.current;
      if (p && p.pauseVideo) { try { p.pauseVideo(); } catch (_) { /* ignore */ } }
      setIsPlaying(false);
      setSleepMinutes(0); setSleepEndsAt(null);
      sleepTimerRef.current = null;
    }, minutes * 60 * 1000);
  }, []);

  // Media Session API: lock-screen title, artwork and hardware play/pause/seek controls.
  useEffect(() => {
    if (typeof window === "undefined" || typeof navigator === "undefined" || !navigator.mediaSession) return undefined;

    const session = navigator.mediaSession;
    const setAction = (name, handler) => {
      try { session.setActionHandler(name, handler); } catch (_) { /* unsupported action */ }
    };

    if (!current || !MEDIA_SESSION_MODES.has(mode)) {
      try { session.playbackState = current && mode === "video" ? "none" : "none"; } catch (_) { /* ignore */ }
      ["play", "pause", "stop", "seekbackward", "seekforward", "seekto", "previoustrack", "nexttrack"].forEach((action) => setAction(action, null));
      return undefined;
    }

    try {
      session.metadata = new window.MediaMetadata({
        title: safeTitle(current, mode),
        artist: safeArtist(current, mode),
        album: safeAlbum(current, mode),
        artwork: artworkFor(current, mode),
      });
      session.playbackState = isPlaying ? "playing" : "paused";
    } catch (_) { /* ignore */ }

    setAction("play", () => { if (!isPlayingRef.current) toggle(); });
    setAction("pause", () => { if (isPlayingRef.current) toggle(); });
    setAction("stop", () => stop());

    if (mode === "track" || mode === "yt-audio") {
      setAction("seekbackward", (details) => seek(Math.max(0, getCurrentPlaybackTime() - (details?.seekOffset || 10))));
      setAction("seekforward", (details) => seek(Math.min(duration || Number.MAX_SAFE_INTEGER, getCurrentPlaybackTime() + (details?.seekOffset || 10))));
      setAction("seekto", (details) => {
        if (Number.isFinite(details?.seekTime)) seek(details.seekTime);
      });
    } else {
      setAction("seekbackward", null);
      setAction("seekforward", null);
      setAction("seekto", null);
    }

    return undefined;
  }, [current, mode, isPlaying, duration, toggle, stop, seek, getCurrentPlaybackTime]);

  // Keep lock-screen position state fresh for seekable music.
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.mediaSession?.setPositionState) return;
    if (!current || !(mode === "track" || mode === "yt-audio") || !duration || duration <= 0) return;
    try {
      navigator.mediaSession.setPositionState({
        duration,
        playbackRate: 1,
        position: Math.min(Math.max(progress || 0, 0), duration),
      });
    } catch (_) { /* ignore */ }
  }, [current, mode, progress, duration]);

  // Do not tear audio down when the tab goes hidden or the phone is locked.
  // Some mobile browsers briefly pause hidden audio; this reasserts playback for user-started radio/MP3 streams.
  // This is the SINGLE recovery handler — the onPause listener defers to this.
  useEffect(() => {
    let recoveryTimer = null;

    const keepDirectAudioAlive = () => {
      const activeMode = modeRef.current;
      if (!HTML_AUDIO_MODES.has(activeMode)) return;
      const el = audioRef.current;
      if (!el || !currentRef.current || userPausedRef.current) return;
      if (el.paused && el.src) {
        // Clear any pending timer to avoid double-fires.
        if (recoveryTimer) clearTimeout(recoveryTimer);
        recoveryTimer = setTimeout(() => {
          recoveryTimer = null;
          if (!userPausedRef.current && el.paused && currentRef.current && HTML_AUDIO_MODES.has(modeRef.current)) {
            el.play()
              .then(() => setIsPlaying(true))
              .catch(() => { /* browser truly won't allow it */ });
          }
        }, 300);
      }
    };

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", keepDirectAudioAlive);
    }
    if (typeof window !== "undefined") {
      window.addEventListener("focus", keepDirectAudioAlive);
      window.addEventListener("pageshow", keepDirectAudioAlive);
    }
    // While hidden, some mobile browsers pause audio without further events.
    // Poll briefly after hiding so the stream resumes if the browser allows it.
    let pollInterval = null;
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") {
          if (pollInterval) clearInterval(pollInterval);
          let attempts = 0;
          pollInterval = setInterval(() => {
            attempts += 1;
            const el = audioRef.current;
            const done = !el || attempts > 10 || userPausedRef.current
              || !currentRef.current || !HTML_AUDIO_MODES.has(modeRef.current);
            if (done || !el.paused) {
              clearInterval(pollInterval);
              pollInterval = null;
              return;
            }
            el.play().then(() => setIsPlaying(true)).catch(() => { /* not allowed */ });
          }, 1000);
        } else if (pollInterval) {
          clearInterval(pollInterval);
          pollInterval = null;
        }
      });
    }
    return () => {
      if (recoveryTimer) clearTimeout(recoveryTimer);
      if (pollInterval) clearInterval(pollInterval);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", keepDirectAudioAlive);
      }
      if (typeof window !== "undefined") {
        window.removeEventListener("focus", keepDirectAudioAlive);
        window.removeEventListener("pageshow", keepDirectAudioAlive);
      }
    };
  }, []);

  // App/browser title mirrors the active audio for better OS task switcher context.
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!current || mode === "video") {
      document.title = "Recho Music — NeurAlpha Music";
      return;
    }
    document.title = `${safeTitle(current, mode)} — Recho Music`;
  }, [current, mode]);

  useEffect(() => () => { if (sleepTimerRef.current) clearTimeout(sleepTimerRef.current); }, []);

  return (
    <PlayerContext.Provider
      value={{
        current, mode, isPlaying, volume, setVolume,
        playStation, playVideo, playTrack, playYouTubeMusic,
        toggle, seek, stop,
        expanded, setExpanded,
        sleepMinutes, sleepEndsAt, setSleepTimer,
        progress, duration,
        ytReady, registerYTPlayer, notifyYTError,
        backgroundAudioReady,
      }}
    >
      {children}
    </PlayerContext.Provider>
  );
}

export const usePlayer = () => useContext(PlayerContext);
